import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Mic } from "lucide-react";
import { createInvoice, getAccountingSettings, listProjectsForInvoicing } from "@/lib/accounting/accounting.functions";
import { fmtMoney } from "@/lib/accounting/exports";
import { toast } from "sonner";
import { DictationDialog, type DictationFields } from "@/components/dictation/DictationDialog";

export const Route = createFileRoute("/_authenticated/admin/accounting/invoices/new")({
  validateSearch: (s: Record<string, unknown>) => ({
    projectId: typeof s.projectId === "string" ? s.projectId : undefined,
  }),
  component: NewInvoice,
});

type Line = {
  kind: "labor" | "materials" | "change_order" | "discount" | "other";
  description: string;
  quantity: number;
  unit_price: number;
  taxable: boolean;
};

function NewInvoice() {
  const { projectId } = Route.useSearch();
  const nav = useNavigate();
  const settingsFn = useServerFn(getAccountingSettings);
  const projectsFn = useServerFn(listProjectsForInvoicing);
  const createFn = useServerFn(createInvoice);

  const settingsQ = useQuery({ queryKey: ["accounting", "settings"], queryFn: () => settingsFn() });
  const projectsQ = useQuery({ queryKey: ["accounting", "projects-mini"], queryFn: () => projectsFn() });

  const [selectedProjectId, setSelectedProjectId] = useState<string>(projectId ?? "");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [address, setAddress] = useState("");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState<string>("");
  const [kind, setKind] = useState<"deposit" | "progress" | "final" | "standard">("standard");
  const [taxRate, setTaxRate] = useState<number>(0.055);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("Payment due within terms specified. Late payments subject to 1.5%/mo finance charge.");
  const [lines, setLines] = useState<Line[]>([
    { kind: "labor", description: "Labor", quantity: 1, unit_price: 0, taxable: true },
  ]);
  const [saving, setSaving] = useState(false);
  const [showDictate, setShowDictate] = useState(false);

  // hydrate from settings + project selection (side effects — must be useEffect, not useMemo)
  useEffect(() => {
    if (!settingsQ.data) return;
    if (taxRate === 0.055 && Number(settingsQ.data.default_tax_rate) !== 0.055) {
      setTaxRate(Number(settingsQ.data.default_tax_rate));
    }
    if (!dueDate) {
      const d = new Date();
      d.setDate(d.getDate() + Number(settingsQ.data.default_payment_terms_days || 15));
      setDueDate(d.toISOString().slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQ.data]);

  useEffect(() => {
    if (!selectedProjectId || !projectsQ.data) return;
    const p: any = projectsQ.data.find((x: any) => x.id === selectedProjectId);
    if (!p) return;
    if (!customerName) setCustomerName(p.customer_name || "");
    if (!customerEmail) setCustomerEmail(p.customer_email || "");
    if (!address) setAddress([p.address, p.city, p.state, p.zip].filter(Boolean).join(", "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, projectsQ.data]);

  const subtotal = lines.reduce((s, l) => s + l.quantity * l.unit_price, 0);
  const taxable = lines.filter((l) => l.taxable && l.kind !== "discount").reduce((s, l) => s + l.quantity * l.unit_price, 0);
  const tax = Math.round(taxable * taxRate * 100) / 100;
  const total = subtotal + tax;

  const updateLine = (i: number, patch: Partial<Line>) => setLines((arr) => arr.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = (kind: Line["kind"] = "labor") =>
    setLines((arr) => [...arr, { kind, description: kind === "discount" ? "Discount" : "", quantity: 1, unit_price: 0, taxable: kind !== "discount" }]);
  const rmLine = (i: number) => setLines((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!customerName.trim()) { toast.error("Customer name required"); return; }
    if (!lines.length) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const res = await createFn({
        data: {
          project_id: selectedProjectId || null,
          customer_name: customerName,
          customer_email: customerEmail || null,
          property_address: address || null,
          issue_date: issueDate,
          due_date: dueDate || undefined,
          kind,
          tax_rate: taxRate,
          discount_amount: 0,
          notes: notes || null,
          terms,
          line_items: lines.map((l, i) => ({
            kind: l.kind,
            description: l.description || l.kind,
            quantity: l.quantity,
            unit_price: l.kind === "discount" ? -Math.abs(l.unit_price) : l.unit_price,
            taxable: l.kind === "discount" ? false : l.taxable,
            sort_order: i,
          })),
        },
      });
      toast.success(`Invoice ${res.invoice_number} created`);
      nav({ to: "/admin/accounting/invoices/$id", params: { id: res.id } });
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-serif text-2xl text-white">New invoice</h2>
        <button
          onClick={() => setShowDictate(true)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-amber-300 text-zinc-950 hover:bg-amber-200"
        >
          <Mic className="size-3.5" /> Dictate invoice
        </button>
      </div>

      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 grid md:grid-cols-2 gap-4">
        <Field label="Project (optional)">
          <select value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)} className={inputCls}>
            <option value="">— None —</option>
            {(projectsQ.data ?? []).map((p: any) => (
              <option key={p.id} value={p.id}>{p.customer_name || p.project_name} — {p.project_name}</option>
            ))}
          </select>
        </Field>
        <Field label="Invoice kind">
          <select value={kind} onChange={(e) => setKind(e.target.value as any)} className={inputCls}>
            <option value="standard">Standard</option>
            <option value="deposit">Deposit</option>
            <option value="progress">Progress</option>
            <option value="final">Final</option>
          </select>
        </Field>
        <Field label="Customer name">
          <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Customer email">
          <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Property address" className="md:col-span-2">
          <input value={address} onChange={(e) => setAddress(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Issue date">
          <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Due date">
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Tax rate (e.g. 0.055 = 5.5%)">
          <input type="number" step="0.001" value={taxRate} onChange={(e) => setTaxRate(Number(e.target.value))} className={inputCls} />
        </Field>
      </section>

      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-white">Line items</h3>
          <div className="flex gap-2">
            <button onClick={() => addLine("labor")} className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-zinc-300 flex items-center gap-1"><Plus className="size-3" /> Labor</button>
            <button onClick={() => addLine("materials")} className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-zinc-300 flex items-center gap-1"><Plus className="size-3" /> Materials</button>
            <button onClick={() => addLine("change_order")} className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-zinc-300 flex items-center gap-1"><Plus className="size-3" /> Change order</button>
            <button onClick={() => addLine("discount")} className="text-xs px-2.5 py-1 rounded-full border border-amber-300/30 text-amber-300 flex items-center gap-1"><Plus className="size-3" /> Discount</button>
          </div>
        </div>

        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <select value={l.kind} onChange={(e) => updateLine(i, { kind: e.target.value as any })} className={`${inputCls} col-span-2`}>
                <option value="labor">Labor</option>
                <option value="materials">Materials</option>
                <option value="change_order">Change order</option>
                <option value="discount">Discount</option>
                <option value="other">Other</option>
              </select>
              <input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder="Description" className={`${inputCls} col-span-5`} />
              <input type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(i, { quantity: Number(e.target.value) })} className={`${inputCls} col-span-1 text-right`} />
              <input type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) })} className={`${inputCls} col-span-2 text-right`} />
              <div className="col-span-1 text-right text-sm text-zinc-300">{fmtMoney(l.quantity * l.unit_price)}</div>
              <button onClick={() => rmLine(i)} className="col-span-1 text-zinc-500 hover:text-red-400 grid place-items-center"><Trash2 className="size-4" /></button>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 mt-4 pt-4 space-y-1 text-sm max-w-xs ml-auto">
          <Row k="Subtotal" v={fmtMoney(subtotal)} />
          <Row k={`Tax (${(taxRate * 100).toFixed(2)}%)`} v={fmtMoney(tax)} />
          <Row k="Total" v={fmtMoney(total)} bold />
        </div>
      </section>

      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 grid md:grid-cols-2 gap-4">
        <Field label="Notes (visible to customer)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={inputCls} />
        </Field>
        <Field label="Terms">
          <textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={3} className={inputCls} />
        </Field>
      </section>

      <div className="flex justify-end gap-2">
        <button onClick={() => nav({ to: "/admin/accounting/invoices" })} className="text-xs px-4 py-2 rounded-full border border-white/10 text-zinc-300">Cancel</button>
        <button onClick={save} disabled={saving} className="text-xs px-5 py-2 rounded-full bg-amber-300 text-zinc-950 font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="size-3 animate-spin inline mr-1" /> : null}
          Save draft
        </button>
      </div>

      {showDictate && (
        <DictationDialog
          mode="invoice"
          onClose={() => setShowDictate(false)}
          onApply={async (fields: DictationFields) => {
            applyInvoiceFields(fields, {
              setCustomerName, setCustomerEmail, setAddress, setKind,
              setDueDate, setTaxRate, setNotes, setTerms, setLines,
            });
            setShowDictate(false);
            toast.success("Filled from dictation. Review and save.");
          }}
          applyLabel="Fill form"
        />
      )}
    </div>
  );
}

function applyInvoiceFields(
  f: DictationFields,
  s: {
    setCustomerName: (v: string) => void;
    setCustomerEmail: (v: string) => void;
    setAddress: (v: string) => void;
    setKind: (v: "deposit" | "progress" | "final" | "standard") => void;
    setDueDate: (v: string) => void;
    setTaxRate: (v: number) => void;
    setNotes: (v: string) => void;
    setTerms: (v: string) => void;
    setLines: (v: Line[]) => void;
  },
) {
  if (typeof f.customer_name === "string" && f.customer_name.trim()) s.setCustomerName(f.customer_name);
  if (typeof f.customer_email === "string" && f.customer_email.trim()) s.setCustomerEmail(f.customer_email);
  if (typeof f.property_address === "string" && f.property_address.trim()) s.setAddress(f.property_address);
  if (typeof f.kind === "string" && ["standard", "deposit", "progress", "final"].includes(f.kind)) s.setKind(f.kind as "deposit" | "progress" | "final" | "standard");
  if (typeof f.due_date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(f.due_date)) s.setDueDate(f.due_date);
  if (typeof f.tax_rate === "number" && f.tax_rate >= 0 && f.tax_rate <= 0.5) s.setTaxRate(f.tax_rate);
  if (typeof f.notes === "string" && f.notes.trim()) s.setNotes(f.notes);
  if (typeof f.terms === "string" && f.terms.trim()) s.setTerms(f.terms);
  const items = Array.isArray(f.line_items) ? (f.line_items as Array<Record<string, unknown>>) : [];
  const mapped: Line[] = items
    .filter((it) => (it.description as string)?.toString().trim() || Number(it.unit_price) !== 0)
    .map((it) => ({
      kind: (["labor", "materials", "change_order", "discount", "other"].includes(String(it.kind))
        ? (it.kind as Line["kind"])
        : "labor"),
      description: String(it.description ?? ""),
      quantity: Number(it.quantity ?? 1) || 1,
      unit_price: Number(it.unit_price ?? 0) || 0,
      taxable: it.taxable === false ? false : true,
    }));
  if (mapped.length) s.setLines(mapped);
}

const inputCls = "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-300/40";

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block ${className ?? ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Row({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? "text-white font-semibold border-t border-white/10 pt-1.5 mt-1.5" : "text-zinc-400"}`}>
      <span>{k}</span><span>{v}</span>
    </div>
  );
}